import { createRef, ReactElement, SyntheticEvent, useRef } from 'react';
import { Option } from '@op/opds.opux';
import { circularIndex } from '../util/calculationUtils';

interface Props {
  options: { value: string; text: string }[];
  onChange: (value: any) => void;
  className?: string;
}

/**
 * Accessible radio button group component. Meant to be wrapped by RadioGroup component.
 */
const useA11yRadioOptionComponents = (props: Props): ReactElement[] => {
  const { options, onChange, className } = props;

  const refs = useRef(options.map(() => createRef<HTMLInputElement>()));
  const currentRefIndex = useRef(0);

  const moveFocus = (delta: number): void => {
    const newRefIndex = circularIndex(
      refs.current.length,
      currentRefIndex.current + delta,
    );
    const newRef = refs.current[newRefIndex];
    newRef.current?.focus();
  };

  const handleClick = (e: SyntheticEvent<HTMLInputElement>): void => {
    onChange((e.target as HTMLInputElement).value);
  };

  const handleKeyDown = (e: SyntheticEvent<HTMLInputElement>): void => {
    const { code: key } = e.nativeEvent as KeyboardEvent;
    switch (key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        moveFocus(1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        moveFocus(-1);
        break;
      case 'Enter':
      case 'Space':
        e.preventDefault();
        onChange(e.currentTarget.value);
        break;
      default:
        break;
    }
  };

  const handleKeyUp = (e: SyntheticEvent<HTMLInputElement>): void => {
    e.preventDefault();
  };

  const handleFocus = (e: SyntheticEvent<HTMLInputElement>): void => {
    // keep track of which ref is currently focused, so that moveFocus() works correctly
    const focusedIndex = refs.current.findIndex((ref) => ref.current === e.currentTarget);
    if (focusedIndex !== -1) {
      currentRefIndex.current = focusedIndex;
    }
  };
  return options.map((option, index) => {
    return (
      <Option
        value={option.value}
        key={`option-${option.value}`}
        inputRef={refs.current[index]}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onClick={handleClick}
        className={className}
      >
        {option.text}
      </Option>
    );
  });
};

export default useA11yRadioOptionComponents;
